import { MobileTasks } from '../../../../backbone-unorganized/tasks/models';
import { isConnected } from '../../../../shared/utils/is';
import {
    showLoadingDialog,
    hideLoadingDialog,
    showOfflineMessage,
    showErrorMessage
} from '../../../../shared/utils/notifications';
import { SelectAccountModal } from '../../../shared/views/SelectAccountModal';
import { BaseFieldView } from '../BaseField/views';
import { EquipmentCollection, MobileIngredients } from '../ingredientEquipmentTemperatureFields/models';
import { MobileFormStructure } from '../MobileFormStructure/models';

export const AutofillPreviousSubmissionExplainer = Backbone.Marionette.Layout.extend({
    template: '#tpl_autofill_previous_submission_explainer',

    initialize: function (options) {
        options || (options = {});
        $.extend(this, options);
    }
});

export const AssociateTaskExplainer = Backbone.Marionette.Layout.extend({
    template: '#tpl_associate_task_explainer',

    initialize: function (options) {
        options || (options = {});
        $.extend(this, options);
    }
});

export const AccountFieldView = BaseFieldView.extend({
    _most_recent_submission: null, // MobileFormStructure — legacy path only (activity_form_prefill_custom_order off)
    _is_shown: false,
    _eligible_task: null, // MobileTask

    regions: {
        r_account_field_extra_explainer: '.r_account_field_extra_explainer'
    },

    triggers: {
        mobile_form_render_noop: 'mobile_form:render' // tells the MobileForm to re-render itself
    },

    events: {
        'click .clear': 'clear_account',
        'click .fake_input:not(.clear)': 'open_select_account_dialog',
        'click .autofill_form': 'autofill_form',
        'click .deny_autofill': 'deny_autofill',
        'click .associate_task': 'associate_task',
        'click .deny_associate_task': 'deny_associate_task'
    },
    initialize: function (options) {
        AccountFieldView.__super__.initialize.apply(this, arguments);
        // Check if there is an Equipment or Ingredient field.
        const has_temperature_field =
            this.model.collection.findWhere({ type: 'ingredient_temperature' }) ||
            this.model.collection.findWhere({ type: 'equipment_temperature' });
        if (has_temperature_field && !this.form_collection.has_been_submitted()) {
            if (this.model.get('value')) {
                // Initial load, but not when editing a draft, so that we don't loose
                // saved values.
                this._fetch_temperature_data({ silent: true });
            }
            this.listenTo(this.model, 'change:value', () => {
                // Every time the location changes
                this._fetch_temperature_data({ silent: false });
            });
        }
    },
    _fetch_temperature_data: function ({ silent }) {
        const show_error = () => {
            setTimeout(() => {
                if (!isConnected()) {
                    showOfflineMessage();
                } else {
                    showErrorMessage();
                }
            });
        };

        const equipmentPromise = EquipmentCollection.fetch_equipment_for_location(
            this.model.get('value'),
            show_error
        ).then((equipmentCollection) => {
            this.model.trigger('change:equipmentCollection', equipmentCollection, silent);
        });

        const ingredientsPromise = MobileIngredients.fetch_ingredients_for_location(
            this.model.get('value'),
            show_error
        ).then((ingredientCollection) => {
            this.model.trigger('change:ingredientCollection', ingredientCollection, silent);
        });

        showLoadingDialog();
        Promise.allSettled([equipmentPromise, ingredientsPromise]).then(hideLoadingDialog, hideLoadingDialog);
    },
    templateHelpers: function () {
        var super_helpers = AccountFieldView.__super__.templateHelpers.call(this);
        var additional_helper_vars = {
            address_more_info: null
        };
        var acct = this.model.get('account');
        if (acct && acct.address && acct.city) {
            var address_str = `${acct.address}, ${acct.city}`;
            address_str += acct.state ? ', ' + acct.state : '';
            address_str += acct.phone ? ', ' + acct.phone : '';
            additional_helper_vars.address_more_info = address_str;
        }
        $.extend(super_helpers, additional_helper_vars);
        return super_helpers;
    },

    onRender: function () {
        if (!this._is_shown) {
            return;
        }

        // First, we'll check to see if there are any eligible tasks we can take over, then (regardless of what they
        // answer of if we prompt them), we'll ask if they want to prefill the submission.

        // fire _check_for_previous_submissions() from render OR show - never both
        if ($f.flags.get_flag('activity_form_prefill_custom_order')) {
            this._check_for_account_prompts();
        } else {
            this._check_for_task_association_eligability();
        }
    },

    onShow: function () {
        this._is_shown = true;
        if ($f.flags.get_flag('activity_form_prefill_custom_order')) {
            this._check_for_account_prompts();
        } else {
            this._check_for_task_association_eligability();
        }
    },

    /**
     * Run the post-selection prompts: task association first, then prefill.
     *
     * The task-association path already chains into the prefill check, so only call it directly
     * when there was no task prompt. A custom location order recreates this view via a full `reset`
     * after the prompt flags were consumed, so re-checking restores the prefill prompt on the new
     * view (its state lives on the model). ZP-17639.
     */
    _check_for_account_prompts: function () {
        var had_task_prompt = Boolean(this.model._show_associate_task_prompt);
        this._check_for_task_association_eligability();
        if (!had_task_prompt) {
            this._check_for_previous_submissions();
        }
    },

    open_select_account_dialog: function (event) {
        var this_ = this;
        if (!$(event.target).hasClass('fake_input')) {
            return;
        } // android 4.1 trigging multiple clicks at once >:(
        showLoadingDialog();

        $f.mobile.nav.show_modal(SelectAccountModal, {
            form_collection: this.form_collection,
            form_field_model: this.model,
            post_on_select: function (account) {
                this_.model._show_autofill_prompt = true; // tell field to check for a last submission
                this_.model._show_associate_task_prompt = true; // tell field to offer to associate form with task
                if ($f.flags.get_flag('activity_form_prefill_custom_order')) {
                    // drop any cached submission so we re-check for the newly chosen account
                    delete this_.model._most_recent_submission;
                }
                this_.model.set({ value: account.id, account: account.toJSON() });
            }
        });
        return false;
    },

    clear_account: function (event) {
        if (!$(event.target).hasClass('clear')) {
            return;
        } // android 4.1 trigging multiple clicks at once >:(
        this.model.set('value', null);
        this.model.set('account', null);
        this.render();

        return false;
    },

    /**
     * Check for a most recent submission matching this form and the chosen account. Note that this
     * will only run when we have opted-in to it from the account picker modal (by setting model._show_autofill_prompt
     * to true). If this runs successfully, it will show a button in the template asking the user if they would like
     * to load the previous submission into the form.
     *
     * The arming flag and fetched submission live on the model, not the view, so they survive the
     * full-form `reset` that a custom location order triggers on selection. The flag is only cleared
     * once the user accepts or denies.
     */
    _check_for_previous_submissions: function () {
        var this_ = this;
        var collection = $f.mobile.get_window_collection();

        if (!collection) {
            return;
        }

        if (!isConnected()) {
            return;
        }

        if (!this.model._show_autofill_prompt) {
            return;
        } // only continue while the prefill prompt is still pending
        if (collection.has_been_submitted()) {
            return;
        } // make sure we're not in history view
        if (!collection.metadata.get('is_copy_enabled')) {
            return;
        } // make sure we're allowed to copy submissions
        if (!this.model.get('account')) {
            return;
        } // shouldn't happen, but just in case

        if ($f.flags.get_flag('activity_form_prefill_custom_order')) {
            // already fetched so just re-show the prompt
            if (this.model._most_recent_submission) {
                this._render_autofill_explainer();
                return;
            }

            this.model.collection.get_previous_submissions({
                account_field: this.model,
                success: function (submissions) {
                    if (!submissions || !submissions.length) {
                        // nothing to copy - stop prompting so later re-renders don't keep re-fetching
                        delete this_.model._show_autofill_prompt;
                        return;
                    }

                    // cache on the model (as a MobileFormStructure) so it survives a re-render
                    this_.model._most_recent_submission = new MobileFormStructure(
                        submissions.at(0).get('form_structure').toJSON()
                    );

                    this_._render_autofill_explainer();
                },
                error: function () {}
            });
            return;
        }

        delete this.model._show_autofill_prompt;

        this.model.collection.get_previous_submissions({
            account_field: this.model,
            success: function (submissions) {
                if (!submissions || !submissions.length) {
                    return;
                }

                this_._most_recent_submission = submissions.at(0).get('form_structure');

                // convert to FormStructure to MobileFormStructure
                this_._most_recent_submission = new MobileFormStructure(this_._most_recent_submission.toJSON());

                if (!this_.r_account_field_extra_explainer) {
                    return;
                }
                var most_recent_smtedata = this_._most_recent_submission.smetadata;
                var submitted_at = moment(new Date(most_recent_smtedata.get('date_submitted').$date))
                    .tz(most_recent_smtedata.get('time_zone'))
                    .format('dddd, MMM D [at] h:mma');

                this_._show_explainer();
                this_.r_account_field_extra_explainer.show(
                    new AutofillPreviousSubmissionExplainer({
                        templateHelpers: function () {
                            return {
                                account_name: this_.model.get('account') ? this_.model.get('account').name : '',
                                last_submission_author:
                                    this_._most_recent_submission.smetadata.get('created_by').display_name,
                                last_submission_time: submitted_at
                            };
                        }
                    })
                );
            },
            error: function () {
                // nothing to do
            }
        });
    },

    /**
     * Show the "load previous submission" prompt from the submission cached on the model.
     */
    _render_autofill_explainer: function () {
        var this_ = this;
        var most_recent_submission = this.model._most_recent_submission;
        if (!most_recent_submission) {
            return;
        }
        if (!this.r_account_field_extra_explainer) {
            return;
        }

        var most_recent_smetadata = most_recent_submission.smetadata;
        var submitted_at = moment(new Date(most_recent_smetadata.get('date_submitted').$date))
            .tz(most_recent_smetadata.get('time_zone'))
            .format('dddd, MMM D [at] h:mma');

        this._show_explainer();
        this.r_account_field_extra_explainer.show(
            new AutofillPreviousSubmissionExplainer({
                templateHelpers: function () {
                    return {
                        account_name: this_.model.get('account') ? this_.model.get('account').name : '',
                        last_submission_author: most_recent_submission.smetadata.get('created_by').display_name,
                        last_submission_time: submitted_at
                    };
                }
            })
        );
    },

    _check_for_task_association_eligability: function () {
        var this_ = this;
        this._close_explainer();

        if (!isConnected()) {
            return;
        }

        if (!this.model._show_associate_task_prompt) {
            return;
        }

        delete this.model._show_associate_task_prompt;

        var account = this.model.get('account');

        if (!account) {
            return;
        }

        var eligable_tasks = new MobileTasks(null, {
            cache_enabled: false,
            url: `/api/v1/forms/get_template_tasks/${this.model.collection.metadata.get('rdbms_id')}?account_id=${
                account.id
            }`
        });

        eligable_tasks.fetch({
            success: function () {
                if (!eligable_tasks.length) {
                    return this_._check_for_previous_submissions();
                }

                this_._eligible_task = eligable_tasks.at(0);

                // if we are the user that should be fulfilling this task, let's not prompt them and just associate
                // it for them.
                if (this_._eligible_task.get('assignee').id === $f.active_user.id) {
                    this_._associate_task(this_._eligible_task);
                    return this_._check_for_previous_submissions();
                }

                this_._show_explainer();
                this_.r_account_field_extra_explainer.show(
                    new AssociateTaskExplainer({
                        templateHelpers: function () {
                            return {
                                explainer_text: _t('mobile_forms_task_on_behalf', {
                                    user: this_._eligible_task.get('assignee').get_display_name()
                                })
                            };
                        }
                    })
                );
            },
            error: function () {
                // do nothing
                this_._check_for_previous_submissions();
            }
        });
    },

    autofill_form: function (event) {
        var this_ = this;

        if ($f.flags.get_flag('activity_form_prefill_custom_order')) {
            var most_recent_submission = this.model._most_recent_submission;
            if (!most_recent_submission) {
                return;
            }
            delete this.model._show_autofill_prompt;
            delete this.model._most_recent_submission;
            showLoadingDialog();
            var active_collection = $f.mobile.get_window_collection();
            // setTimeout gives time for the loading spinner (above) to show
            setTimeout(function () {
                try {
                    active_collection.autofill_from_previous_submission(most_recent_submission);
                    active_collection.trigger('change');
                    this_.trigger('mobile_form:render');
                } catch (err) {
                    $f.mobile.logging.log_error('autofill_from_previous_submission failed', { error: err });
                    // This catch used to swallow the crash silently, leaving a blank form (ZP-17639).
                    // Surface the error and restore the prompt before re-rendering, so the user is told
                    // and the prompt comes back even if the re-render itself throws.
                    showErrorMessage(_t('mobile_html_something_went_wrong_try_again'));
                    this_.model._show_autofill_prompt = true;
                    this_.model._most_recent_submission = most_recent_submission;
                    this_.trigger('mobile_form:render');
                } finally {
                    hideLoadingDialog();
                }
            }, 100);
            return false;
        }

        if (!this._most_recent_submission) {
            return;
        }
        delete this.model._show_autofill_prompt;
        showLoadingDialog();
        var legacy_collection = $f.mobile.get_window_collection();

        setTimeout(function () {
            try {
                legacy_collection.autofill_from_previous_submission(this_._most_recent_submission);
                legacy_collection.trigger('change');
                this_.trigger('mobile_form:render');
            } catch (err) {
                $f.mobile.logging.log_error('autofill_from_previous_submission failed', { error: err });
            } finally {
                hideLoadingDialog();
            }
        }, 100);
        return false;
    },

    _close_explainer: function () {
        this.$el.find('.r_account_field_extra_explainer').hide();
        if (!this.r_account_field_extra_explainer) {
            return;
        }

        this.r_account_field_extra_explainer.close();
    },

    _show_explainer: function () {
        this.$el.find('.r_account_field_extra_explainer').show();
    },

    deny_autofill: function (event) {
        this._close_explainer();
        delete this.model._show_autofill_prompt;
        if ($f.flags.get_flag('activity_form_prefill_custom_order')) {
            delete this.model._most_recent_submission;
        }

        return false;
    },

    associate_task: function (event) {
        delete this.model._show_associate_task_prompt;
        this._associate_task(this._eligible_task);

        // Now that we asked this, we should ask if they want to prefill
        this._check_for_previous_submissions();

        return false;
    },

    _associate_task: function (task) {
        this._close_explainer();

        if (!this._eligible_task) {
            return; // should never happen, but just in case
        }

        const collection = $f.mobile.get_window_collection();
        collection.smetadata.set('task', {
            id: task.id,
            title: task.get('title'),
            account_id: this.model.get('account').id
        });

        // The save scheduled by picking the location can land before this lookup resolves,
        // so save the task info here to avoid that race.
        $f.mobile.save_active_collection(collection);
    },

    deny_associate_task: function (event) {
        delete this.model._show_associate_task_prompt;
        this._close_explainer();

        // Now that we asked this, we should ask if they want to prefill
        this._check_for_previous_submissions();

        return false;
    }
});
